'use client';

import { useEffect, useRef, useState } from 'react';

interface NavigatorWithExtras extends Navigator {
  connection?: { saveData?: boolean; effectiveType?: string; downlink?: number };
  deviceMemory?: number;
}

const checkCapability = (): boolean => {
  const prefersReducedMotion = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const nav = navigator as NavigatorWithExtras;
  const saveData = nav.connection?.saveData === true;

  // Use downlink (Mbps) when available — catches slow 4G and accounts for large video files.
  // Fall back to effectiveType for browsers that don't expose downlink.
  const slowConnection =
    nav.connection?.downlink && nav.connection.downlink < 1.5
      ? true
      : nav.connection?.effectiveType === 'slow-2g' ||
        nav.connection?.effectiveType === '2g' ||
        nav.connection?.effectiveType === '3g';

  const lowMemory = nav.deviceMemory && nav.deviceMemory < 4;

  return !prefersReducedMotion && !saveData && !slowConnection && !lowMemory;
};

export const useVideoCapability = (videoId: string | undefined) => {
  const [shouldAttemptVideo, setShouldAttemptVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!videoId) return;
    if (checkCapability()) setShouldAttemptVideo(true);
  }, [videoId]);

  useEffect(() => {
    if (!shouldAttemptVideo) return;

    const iframe = videoWrapperRef.current?.querySelector('iframe');
    if (!iframe) return;

    iframe.setAttribute('tabindex', '-1');

    const handleLoad = () => setVideoReady(true);
    iframe.addEventListener('load', handleLoad, { once: true });

    return () => iframe.removeEventListener('load', handleLoad);
  }, [shouldAttemptVideo]);

  return { shouldAttemptVideo, videoReady, videoWrapperRef };
};
